// src/services/reportService.js
import { buildApiUrl } from '../config/environment';

/**
 * Search tenancies using the backend search-tenancies endpoint.
 *
 * @param {Object}   params
 * @param {string[]} [params.status]              - Array of tenancy statuses (e.g. ['active'])
 * @param {boolean}  [params.negativeBalanceOnly] - Only return tenancies with a negative ledger balance
 * @param {number}   [params.agencyId]            - Restrict results to properties owned by this agency
 * @param {number}   [params.page]                - 1-based page number
 * @param {number}   [params.pageSize]            - Results per page (default 20)
 * @returns {Promise<{tenancies: Array, total: number, page: number, page_size: number, total_pages: number}>}
 */
export const searchTenancies = async ({
  status,
  negativeBalanceOnly = false,
  agencyId,
  page = 1,
  pageSize = 20,
} = {}) => {
  const token =
    sessionStorage.getItem('access_token') || sessionStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const body = {
    page,
    page_size: pageSize,
  };

  if (status && status.length > 0) {
    body.status = status;
  }

  if (negativeBalanceOnly) {
    body.negative_balance_only = true;
  }

  // Send agency_id so the backend scopes results to this agency's properties
  if (agencyId) {
    body.agency_id = Number(agencyId);
  }

  const response = await fetch(buildApiUrl('/search-tenancies'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication failed – token may be expired');
    }
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Request failed with status ${response.status}`);
  }

  return response.json();
};

/**
 * Fetch paginated rental listings for all properties owned by an agency.
 * Current tenancy data (renters, agreement, ledger balance) is included per listing.
 *
 * @param {Object}  params
 * @param {number}  params.agencyId    - Agency ID (required)
 * @param {number}  [params.page]      - 1-based page number (default 1)
 * @param {number}  [params.pageSize]  - Results per page (default 20)
 * @param {boolean} [params.overdueOnly] - Only listings with a negative ledger balance
 * @param {string}  [params.status]    - Filter by current tenancy status (active|inactive|pending|available|expired|terminated)
 * @returns {Promise<{listings: Array, total: number, page: number, page_size: number, total_pages: number}>}
 */
export const fetchRentalListingsByAgency = async ({
  agencyId,
  page = 1,
  pageSize = 20,
  overdueOnly = false,
  status = '',
} = {}) => {
  const token =
    sessionStorage.getItem('access_token') || sessionStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (overdueOnly) params.set('overdue_only', 'true');
  if (status) params.set('status', status);

  const response = await fetch(
    buildApiUrl(`/agencies/${agencyId}/rental-listings?${params.toString()}`),
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication failed – token may be expired');
    }
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Request failed with status ${response.status}`);
  }

  return response.json();
};
